import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { adminBulkRbacService } from '../services'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'
import useAuthStore from '../store/authStore'

const BulkRoleCreationPage = () => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.BULK_ROLE_CREATION,
    'write',
  )

  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [tokensLoading, setTokensLoading] = useState(true)
  const [tokens, setTokens] = useState([])
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)

  const loadTokens = useCallback(async () => {
    try {
      setTokensLoading(true)
      const data = await adminBulkRbacService.listAccessTokens()
      setTokens(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load access tokens')
    } finally {
      setTokensLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTokens()
  }, [loadTokens])

  const onSubmit = async () => {
    if (!file) {
      setError('Choose a CSV file first')
      return
    }
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const data = await adminBulkRbacService.importRoleCreations(file)
      setResult(data)
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Import failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h5" gutterBottom>
        Bulk role creation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload CSV with columns <strong>role_name</strong> and <strong>access</strong>. Multiple
        rows with the same role name combine access tokens into one custom role.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Button variant="outlined" component="label">
              Choose CSV
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null)
                  setResult(null)
                }}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {file ? file.name : 'No file selected'}
            </Typography>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
              disabled={!canWrite || !file || loading}
              onClick={onSubmit}
            >
              Import
            </Button>
          </Box>
          {!canWrite ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              You need bulk role creation write permission to import.
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {result?.summary ? (
        <Alert severity={result.summary.failedRows ? 'warning' : 'success'} sx={{ mb: 2 }}>
          {result.summary.rolesCreated} role(s) created; {result.summary.successRows} of{' '}
          {result.summary.totalRows} rows succeeded.
        </Alert>
      ) : null}

      {result?.roles?.length ? (
        <Paper sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>
            Roles
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Role name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.roles.map((role) => (
                  <TableRow key={role.role_name}>
                    <TableCell>{role.role_name}</TableCell>
                    <TableCell>{role.code || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={role.status}
                        color={role.status === 'success' ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>{role.reason || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}

      {result?.rows?.length ? (
        <Paper sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>
            Row results
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Role name</TableCell>
                  <TableCell>Access</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.rows.map((row) => (
                  <TableRow key={`${row.row}-${row.role_name}-${row.access}`}>
                    <TableCell>{row.row}</TableCell>
                    <TableCell>{row.role_name}</TableCell>
                    <TableCell>{row.access}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.status}
                        color={row.status === 'success' ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>{row.reason || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}

      <Typography variant="h6" gutterBottom>
        Access token catalog
      </Typography>
      {tokensLoading ? (
        <CircularProgress size={28} />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Token</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Label</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map((token) => (
                <TableRow key={token.token}>
                  <TableCell>
                    <code>{token.token}</code>
                  </TableCell>
                  <TableCell>{token.subject}</TableCell>
                  <TableCell>{token.action}</TableCell>
                  <TableCell>{token.label}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  )
}

export default BulkRoleCreationPage
